//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import type {
  ExecutionFollowUp,
  ExecutionReceiptStatus,
  ExecutionTerminalEvent,
  StoredExecutionReceipt,
} from '../../../types/executionReceipt';

const OUTCOME: Record<ExecutionReceiptStatus, string> = {
  completed: 'completed',
  partial: 'partial',
  failed: 'failed',
  cancelled: 'cancelled',
};

const FOLLOW_UP: Partial<Record<ExecutionFollowUp, string>> = {
  needs_new_plan: 'A new plan is needed for the remaining work.',
  user_review: 'Please review the result.',
};

/**
 * One short sentence for the polite live region when an execution's receipt
 * arrives, so a screen-reader user hears that the run finished, how it ended and
 * whether anything is needed from them. The outcome and the follow-up are
 * spoken separately, the same way the card shows them.
 */
export const receiptAnnouncement = (
  receipt: StoredExecutionReceipt,
  terminalStatus?: string | null,
): string => {
  const outcome = OUTCOME[receipt.status] ?? receipt.status;
  const followUp =
    receipt.follow_up ??
    (terminalStatus === 'needs_new_plan' ? 'needs_new_plan' : undefined);
  const tail = followUp ? FOLLOW_UP[followUp] : undefined;
  return `Execution finished: ${outcome}.${tail ? ` ${tail}` : ''}`;
};

/** The same, for the end-of-run notice shown when no receipt covers the run. */
export const terminalAnnouncement = (event: ExecutionTerminalEvent): string => {
  if (event.status === 'needs_new_plan') {
    return 'Execution finished. A new plan is needed.';
  }
  if (event.status === 'unverified') {
    return event.refused_tools?.length > 0
      ? 'Execution finished. Some calls did not run and the result is not verified.'
      : 'Execution finished. No receipt is available.';
  }
  return 'Execution ended.';
};
